/*************************************************************************
 *  ADOBE CONFIDENTIAL
 *
 *  Copyright 2021 Adobe
 *  All Rights Reserved.
 *
 *  NOTICE:  All information contained herein is, and remains
 *  the property of Adobe and its suppliers, if any. The intellectual
 *  and technical concepts contained herein are proprietary to Adobe
 *  and its suppliers and are protected by all applicable intellectual
 *  property laws, including trade secret and copyright laws.
 *  Dissemination of this information or reproduction of this material
 *  is strictly forbidden unless prior written permission is obtained
 *  from Adobe.
 **************************************************************************/

import { updatePreSearchRecommendations } from '../../../actions/searchActions';
import React, { FC, ReactElement, useEffect, useState } from 'react';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import { ConnectedProps, connect } from 'react-redux';
import SearchDropDownRecommendationItem from './SearchDropDownRecommendationItem';
import SearchHostContext from '../../../utils/SearchHostContext';
import SearchAnalytics from '../../../analytics/SearchAnalytics';
import HorizontalRuler from '../../HorizontalRuler';
import SearchUtils from '../../../utils/SearchUtils';
import { PRESEARCH_UI_SORT_ORDERS } from '../../../containers/SearchResultsPage/SearchConstants';

type OwnProps = Record<string, any>;

type SearchDropDownRecommendationsProps = OwnProps & WrappedComponentProps & PropsFromRedux;

const SearchDropDownRecommendations: FC<SearchDropDownRecommendationsProps> = ({
	searchConfig,
	presearchResultsPayload,
	recommendationsGroups,
	searchAppMetadataList,
	updatePreSearchRecommendations,
}): ReactElement => {
	const searchHostContext = SearchHostContext.getContext();
	const analytics = new SearchAnalytics(searchHostContext);

	const popularSearchTerms = presearchResultsPayload?.popularSearches?.searchResults;
	const [appsSuggestionData, setAppsSuggestionData] = useState<{ groupText: string; recommendations: Array<any> }>({
		groupText: '',
		recommendations: [],
	});

	useEffect(() => {
		const recommendationsFromConfig = searchConfig && searchConfig.ui && searchConfig.ui.preSearchRecommendations;
		recommendationsFromConfig &&
			searchAppMetadataList &&
			!(recommendationsGroups && Object.keys(recommendationsGroups).length) &&
			updatePreSearchRecommendations(recommendationsFromConfig);
	}, [
		searchConfig,
		recommendationsGroups,
		searchAppMetadataList,
		updatePreSearchRecommendations,
		presearchResultsPayload,
	]);

	useEffect(() => {
		let appsSuggestionGroupData: { groupText: string; recommendations: Array<any> } = {
			groupText: '',
			recommendations: [],
		};
		Object.entries(recommendationsGroups).forEach(([groupType, recommendations]) => {
			const groupTypeLowerCase = groupType.toLowerCase();
			let groupedTrendingRecommendations = [];
			groupedTrendingRecommendations = SearchUtils.getTrendingApps(
				recommendations,
				popularSearchTerms?.products,
				searchAppMetadataList,
			);
			if (groupTypeLowerCase === 'suggestions') {
				appsSuggestionGroupData = {
					groupText: 'app.search.presearch.apps&services.title',
					recommendations: groupedTrendingRecommendations,
				};
			}
		});
		setAppsSuggestionData(appsSuggestionGroupData);
	}, [recommendationsGroups]);

	useEffect(() => {
		if (appsSuggestionData.recommendations.length) {
			const updatedRecommendations = appsSuggestionData.recommendations.map((appSuggestion) => {
				const { ffcId } = appSuggestion;
				let actionUrl = undefined;
				if (ffcId) {
					const appMetadata = searchAppMetadataList?.apps ? searchAppMetadataList.apps[ffcId] : {};
					actionUrl = appMetadata?.url;
				}

				return Object.assign({}, appSuggestion, { actionUrl });
			});
			analytics.postPreSearchItemAction({
				recommendationType: 'appRecommendations',
				eventType: 'render',
				uiSortOrder: PRESEARCH_UI_SORT_ORDERS.appRecommendations,
				data: updatedRecommendations,
				isExpress: popularSearchTerms?.isExpress,
				fromModel: !popularSearchTerms?.isEmpty,
				modelVersion: popularSearchTerms?.modelVersion,
				planGroup: popularSearchTerms?.planGroup,
			});
		}
	}, [analytics, appsSuggestionData, popularSearchTerms, searchAppMetadataList]);

	if (
		!appsSuggestionData.groupText ||
		!(Array.isArray(appsSuggestionData.recommendations) && appsSuggestionData.recommendations.length)
	) {
		return null;
	}
	return (
		<>
			<div className='search-dropdown-recommendation'>
				<div
					key={'preSearch_suggestions'}
					className='search-dropdown-recommendation-group'
					role='group'
					aria-labelledby='search-recommendation-group-title'
				>
					{appsSuggestionData.groupText && (
						<h5
							className='search-dropdown-recommendation-group-title'
							id='search-recommendation-group-title'
							role='presentation'
						>
							<FormattedMessage id={appsSuggestionData.groupText} />
						</h5>
					)}
					{appsSuggestionData.recommendations.map((recommendation, index) => (
						<SearchDropDownRecommendationItem
							key={recommendation.id}
							data={recommendation}
							index={index}
							type='appRecommendations'
							count={appsSuggestionData.recommendations.length}
						/>
					))}
					<HorizontalRuler />
				</div>
			</div>
		</>
	);
};

const mapStateToProps = ({ search }: RootState) => ({
	searchConfig: search.searchConfig,
	presearchResultsPayload: search.presearchResultsPayload,
	recommendationsGroups: search.preSearchRecommendations,
	searchAppMetadataList: search.searchAppMetadataList,
});

const mapDispatchToProps = {
	updatePreSearchRecommendations,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(SearchDropDownRecommendations));
